import React, { useEffect, useState, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  Checkbox,
  TextField,
} from "@mui/material";
import { t } from "i18next";
import { toastMessage } from "../../../helper";
import { emissionAvailableCategoriesService } from "../../../services/emissionService";
import { CATEGORY_CODE, TARGET_TYPES } from "../../../constants/enums";
import { primaryColor } from "../../../constants/colorConstants";
import CancelIcon from "@mui/icons-material/Cancel";
import FileAttachmentsDetails from "../../common/FileAttachmentsDetails";

const BaselineYearDialog = (props: any) => {
  const {
    open,
    onClose,
    currentBaseline,
    year,
    emissionData,
    getAvailableCategories,
    categoryData,
    setCategoryData,
    scopes,
    selectedIndex,
    createKey,
  } = props;

  const [cellColumns, setCellColumns] = useState([
    "Baseline year",
    "Emissions(tCO2e)",
    "Included",
    "",
    "Comments",
  ]);

  const [formData, setFormData] = useState<any>({
    attachments: [],
  });

  const [editableCell, setEditableCell] = useState<{
    rowId: string;
    column: string;
  } | null>(null);
  const [editedValue, setEditedValue] = useState<number | string>("");
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [currentRowId, setCurrentRowId] = useState<string | null>(null);
  const hasCategoryDataUpdated = useRef(false);

  useEffect(() => {
    if (hasCategoryDataUpdated.current) {
      getAvailableCategories(
        emissionData,
        TARGET_TYPES.BASELINE,
        currentBaseline.year,
        currentBaseline.scope
      );
      hasCategoryDataUpdated.current = false;
    }
  }, [categoryData]);

  const key = createKey(
    currentBaseline.year,
    currentBaseline.scope,
    currentBaseline.categoryId || 0
  );

  const handleTableCellClick = (rowId: string, attachments: []) => {
    setShowFileUpload(true);
    setCurrentRowId(rowId);
    if (attachments && attachments.length) {
      setFormData({ attachments: [...attachments] });
    } else {
      setFormData({ attachments: [] });
    }
  };

  const handleClose = () => {
    setShowFileUpload(false);
  };

  const handleDoubleClick = (
    rowId: string,
    column: string,
    value: number | string
  ) => {
    setEditableCell({ rowId, column });
    setEditedValue(value);
  };

  const handleValueChange = (event: any) => {
    if (/^\d*\.?\d*$/.test(event.target.value)) {
      setEditedValue(event.target.value);
    }
  };

  const handleValueSave = (rowId: string, column: string) => {
    setCategoryData((prevData: Map<string, any[]>) => {
      const updatedData = new Map(prevData);
      const categories = updatedData.get(key);
      if (categories) {
        const categoryIndex = categories.findIndex(
          (category) => category.id === rowId
        );
        if (categoryIndex !== -1) {
          categories[categoryIndex][column] =
            editedValue !== "" ? editedValue : 0;
          updatedData.set(key, categories);
          hasCategoryDataUpdated.current = true;
        }
      }
      return updatedData;
    });
    setEditableCell(null);
  };

  const handleCheckboxChange = (rowId: string, checked: boolean) => {
    setCategoryData((prevData: Map<string, any[]>) => {
      const updatedData = new Map(prevData);
      const categories = updatedData.get(key);
      if (categories) {
        const categoryIndex = categories.findIndex(
          (category) => category.id === rowId
        );
        if (categoryIndex !== -1) {
          categories[categoryIndex].included = checked;
          updatedData.set(key, categories);
          hasCategoryDataUpdated.current = true;
        }
      }
      return updatedData;
    });
  };

  const handleCommentChange = (rowId: string, comment: string) => {
    setCategoryData((prevData: Map<string, any[]>) => {
      const updatedData = new Map(prevData);
      const categories = updatedData.get(key);
      if (categories) {
        const categoryIndex = categories.findIndex(
          (category) => category.id === rowId
        );
        if (categoryIndex !== -1) {
          categories[categoryIndex].comments = comment;
          updatedData.set(key, categories);
          hasCategoryDataUpdated.current = true;
        }
      }
      return updatedData;
    });
  };

  const handleFileUpload = (files: any[]) => {
    setFormData((prevData: any) => ({
      attachments: files,
    }));

    setCategoryData((prevData: Map<string, any[]>) => {
      const updatedData = new Map(prevData);
      const categories = updatedData.get(key);
      if (categories) {
        const categoryIndex = categories.findIndex(
          (category) => category.id === currentRowId
        );
        if (categoryIndex !== -1) {
          categories[categoryIndex].attachments = files.length > 0 ? files : [];
          updatedData.set(key, categories);
          hasCategoryDataUpdated.current = true;
        }
      }
      return updatedData;
    });
  };

  const baseline = currentBaseline;

  const [totalEmissionValue, setTotalEmissionValue] = useState(0);

  useEffect(() => {
    let newValue = 0;
    if (baseline.scope === 1 && selectedIndex > 1) {
      newValue = scopes.get(key)?.scope1 || 0;
    } else if (baseline.scope === 2) {
      newValue = scopes.get(key)?.scope2 || 0;
    } else if (baseline.scope === 3) {
      newValue = scopes.get(key)?.scope3 || 0;
    } else if (baseline.scope === 1 && selectedIndex === 1) {
      newValue = (
        (scopes.get(key)?.scope1 || 0) + (scopes.get(key)?.scope2 || 0)
      ).toFixed(3);
    } else {
      newValue = (
        (scopes.get(key)?.scope1 || 0) +
        (scopes.get(key)?.scope2 || 0) +
        (scopes.get(key)?.scope3 || 0)
      ).toFixed(3);
    }
    setTotalEmissionValue(newValue);
  }, [baseline.scope, scopes, year]);

  const renderCategoryRows = () => {
    const categories =
      categoryData.get(
        createKey(
          currentBaseline.year,
          currentBaseline.scope,
          currentBaseline?.categoryId || 0
        )
      ) || [];
    return (
      categories
        //.filter((eachCategory: any) => shouldDisplayCategory(eachCategory.code))
        .map((eachCategory: any) => (
          <React.Fragment key={eachCategory.id}>
            {eachCategory.code === CATEGORY_CODE.CATEGORY_1_1 && (
              <TableRow>
                <TableCell className="bg-info-100 text-slate-700 text-sm font-bold font-['Proxima Nova'] pt-3 pb-2">
                  Scope 1 :
                </TableCell>
                <TableCell className="bg-info-100" align="right">
                  {scopes.get(key).scope1}
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            )}
            {eachCategory.code === CATEGORY_CODE.CATEGORY_2_1 && (
              <TableRow>
                <TableCell className="bg-info-100 text-slate-700 text-sm font-bold font-['Proxima Nova'] pt-3 pb-2">
                  Scope 2 :
                </TableCell>
                <TableCell className="bg-info-100" align="right">
                  {scopes.get(key).scope2}
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            )}
            {eachCategory.code === CATEGORY_CODE.CATEGORY_3_1 && (
              <TableRow>
                <TableCell className="bg-info-100 text-slate-700 text-sm font-bold font-['Proxima Nova'] pt-3 pb-2">
                  Scope 3 :
                </TableCell>
                <TableCell className="bg-info-100" align="right">
                  {scopes.get(key).scope3}
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            )}
            <TableRow>
              <TableCell>
                {eachCategory.code} {eachCategory.name}
              </TableCell>
              <TableCell
                className={`bg-total-row pw-cat-emissionVal-${eachCategory.id} pw-no-focus-outline`}
                align="right"
                onClick={() =>
                  handleDoubleClick(
                    eachCategory.id,
                    "value",
                    eachCategory.value || 0
                  )
                }
                onKeyDown={(e) => {
                  // if (e.key === "Enter") {
                  //   handleDoubleClick(
                  //     eachCategory.id,
                  //     "value",
                  //     eachCategory.value || 0
                  //   );
                  // }
                }}
                tabIndex={0}
              >
                {editableCell &&
                editableCell.rowId === eachCategory.id &&
                editableCell.column === "value" ? (
                  <TextField
                    value={editedValue}
                    onChange={handleValueChange}
                    onBlur={() => handleValueSave(eachCategory.id, "value")}
                    inputProps={{
                      style: { padding: "0 8px", width: "fixed" },
                    }}
                    autoFocus
                  />
                ) : eachCategory.value ? (
                  eachCategory.value
                ) : (
                  0
                )}
              </TableCell>
              <TableCell
                align="center"
                className="pw-no-focus-outline"
                tabIndex={0}
              >
                <Checkbox
                  sx={{
                    color: "#8897AE",
                    "&.Mui-checked": { color: primaryColor },
                  }}
                  checked={eachCategory.included}
                  onChange={(e) =>
                    handleCheckboxChange(eachCategory.id, e.target.checked)
                  }
                />
              </TableCell>
              <TableCell
                align="center"
                onClick={() =>
                  handleTableCellClick(
                    eachCategory.id,
                    eachCategory.attachments
                  )
                }
                onKeyDown={(e) => {
                  // if (e.key === "Enter") {
                  //   handleTableCellClick(
                  //     eachCategory.id,
                  //     eachCategory.attachments
                  //   );
                  // }
                }}
                tabIndex={0}
                className="cursor-pointer pw-no-focus-outline"
              >
                <img src="assets/icons/FileUpload.svg" />
                {eachCategory.attachments &&
                eachCategory.attachments.length > 0 ? (
                  <sup className=" text-xs bg-primary badge">
                    {eachCategory.attachments?.length}
                  </sup>
                ) : (
                  ""
                )}
              </TableCell>
              <TableCell className="pw-no-focus-outline" tabIndex={0}>
                <textarea
                  name="comment"
                  placeholder="Enter"
                  className="pw-textarea"
                  value={eachCategory.comments || ""}
                  onChange={(e) =>
                    handleCommentChange(eachCategory.id, e.target.value)
                  }
                ></textarea>
              </TableCell>
            </TableRow>
          </React.Fragment>
        ))
    );
  };

  return (
    <Dialog open={open} className="pw-target-dialog">
      <div className="dialog-content">
        <div className="dialog-header">
          <div>Baseline Year details</div>
          <div onClick={onClose} className="close-icon">
            <CloseIcon />
          </div>
        </div>
        <div className="dialog-body">
          <Card>
            <Table size="small" className="pw-activity-table">
              <TableHead className="pw-activity-table-head">
                <TableRow>
                  {cellColumns.map((eachCell, index) => (
                    <TableCell
                      key={index}
                      className={`pw-activity-list ${
                        index === 0
                          ? "w-[513px]"
                          : index === 1
                          ? " w-[124px] bg-total-row"
                          : index === 2
                          ? "w-[78px]"
                          : index === 3
                          ? "w-[50px]"
                          : " w-[203px]"
                      }`}
                      align={index === 1 ? "center" : "left"}
                    >
                      {t(eachCell)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell className="bg-info-100">{year || "NA"}</TableCell>
                  <TableCell className="bg-info-100" align="right">
                    {totalEmissionValue}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                </TableRow>
                {renderCategoryRows()}
              </TableBody>
            </Table>
          </Card>
        </div>
      </div>
      <Dialog
        open={showFileUpload}
        onClose={handleClose}
        className="pw-attachment-dialog"
      >
        <div className="pb-6">
          <div className="pw-header-text">Add attachments</div>
          <div
            onClick={handleClose}
            style={{
              cursor: "pointer",
              textAlign: "right",
              marginTop: "-30px",
              // padding: "10px",
            }}
          >
            <CancelIcon />
          </div>
          <FileAttachmentsDetails
            formData={formData}
            setFormData={(formData: any) =>
              handleFileUpload(formData.attachments)
            }
          ></FileAttachmentsDetails>
        </div>
      </Dialog>
    </Dialog>
  );
};

export default BaselineYearDialog;
